/**
 * PhotoCorner — four L-bracket marks placed at a rectangular frame's
 * corners. Gives any bounded element the feel of a photograph
 * mounted with bronze brackets. Pure decoration — aria-hidden.
 */

interface Props {
  /** Bracket arm length in px. Default 18 — looks balanced on 400-600px frames. */
  size?: number;
  /** Stroke colour. Default bronze. Accepts any CSS colour. */
  color?: string;
  /** Thickness in px. Default 1.5. */
  thickness?: number;
  /** Distance from corner in px. Default 10. */
  inset?: number;
  /** Hide specific corners if needed. */
  omit?: Array<"tl" | "tr" | "bl" | "br">;
}

export function PhotoCorner({
  size = 18,
  color = "rgba(255, 183, 3, 0.85)",
  thickness = 1.5,
  inset = 10,
  omit = [],
}: Props) {
  const common = { width: size, height: size, stroke: color, strokeWidth: thickness, fill: "none" as const };
  const armH = { x1: 0, y1: 0, x2: size, y2: 0 };
  const armV = { x1: 0, y1: 0, x2: 0, y2: size };

  return (
    <>
      {!omit.includes("tl") && (
        <svg aria-hidden viewBox={`0 0 ${size} ${size}`} {...common}
             style={{ position: "absolute", top: inset, left: inset, transform: "rotate(0)" }}>
          <line {...armH} strokeLinecap="square" />
          <line {...armV} strokeLinecap="square" />
        </svg>
      )}
      {!omit.includes("tr") && (
        <svg aria-hidden viewBox={`0 0 ${size} ${size}`} {...common}
             style={{ position: "absolute", top: inset, right: inset, transform: "rotate(90deg)" }}>
          <line {...armH} strokeLinecap="square" />
          <line {...armV} strokeLinecap="square" />
        </svg>
      )}
      {!omit.includes("bl") && (
        <svg aria-hidden viewBox={`0 0 ${size} ${size}`} {...common}
             style={{ position: "absolute", bottom: inset, left: inset, transform: "rotate(-90deg)" }}>
          <line {...armH} strokeLinecap="square" />
          <line {...armV} strokeLinecap="square" />
        </svg>
      )}
      {!omit.includes("br") && (
        <svg aria-hidden viewBox={`0 0 ${size} ${size}`} {...common}
             style={{ position: "absolute", bottom: inset, right: inset, transform: "rotate(180deg)" }}>
          <line {...armH} strokeLinecap="square" />
          <line {...armV} strokeLinecap="square" />
        </svg>
      )}
    </>
  );
}
